import type { Tone } from '../constants/tone';

export const initials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Chip-label casing (Phase 7, docs/Frontend-Redesign-Plan.md). Most stored enum values
 * are already nicely cased ('Active', 'In Progress') and must render byte-for-byte
 * unchanged - only a raw slug/snake_case value ('in_progress') or an all-lowercase,
 * no-space value ('active') gets title-cased, since those are the two shapes seen across
 * the app that were never meant to be shown to a user verbatim. */
export const formatChipLabel = (value: string): string => {
  if (!value) return value;
  const looksRaw = value.includes('_') || (value === value.toLowerCase() && !value.includes(' '));
  if (!looksRaw) return value;
  return value
    .split(/[_\s]+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

export const fmtRelative = (iso: string): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return iso;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
};

// 'secondary' (old Bootstrap colour name) stored directly as 'neutral' below - every
// consumer previously ran these maps' values through a local toneOf() that translated
// 'secondary' -> 'neutral' before rendering, so the rendered chip tone is unchanged; this
// just bakes that translation into the map instead of doing it at each call site, which is
// required for these maps to satisfy Record<string, Tone> ('secondary' is not a Tone member).
export const HEALTH_COLOR: Record<string, Tone> = {
  'On Track': 'success',
  'At Risk': 'warning',
  Blocked: 'danger',
  'Not Started': 'neutral',
};

/** The health to actually SHOW for an initiative - not necessarily what's stored.
 *  inittrack-api's create_initiative defaults every new initiative to health:'On Track'
 *  regardless of status, so anything nobody has touched yet reads as "on track" before any
 *  work has happened. Not-started work has no health to report - Health already has a
 *  'Not Started' member for exactly this - so display (never the stored value itself)
 *  overrides to it whenever status says so, everywhere health is shown, counted, or
 *  colour-coded. */
export const effectiveHealth = (i: { status: string; health: string }): string =>
  i.status === 'Not Started' ? 'Not Started' : i.health;

/** A Program/Portfolio's Continuous Improvement Health - how the change work underneath it
 *  (direct children only, one level - a Portfolio looks at its Programs, not its Projects two
 *  levels down) is actually delivering. A proportion, not a bucket average: with one active
 *  child it's binary (fully healthy or not), with several there's real room for one struggling
 *  child not to sink the whole thing. effectiveHealth() alone handles both the exclusion (it
 *  already returns 'Not Started' when status is) and the bucketing. Returns null - not a fake
 *  0% - when nothing is computable, same convention as milestoneCompletion() below. */
export const ciHealth = (children: { status: string; health: string }[]): number | null => {
  const weights: number[] = children
    .map((c) => effectiveHealth(c))
    .filter((h) => h !== 'Not Started')
    .map((h) => (h === 'On Track' ? 1 : h === 'At Risk' ? 0.5 : 0));
  if (weights.length === 0) return null;
  return Math.round((weights.reduce((a, b) => a + b, 0) / weights.length) * 100);
};

/** The one shared threshold-to-tone rule for a computed percentage score (Continuous
 *  Improvement Health, Operational Health, and any future one) - several pages independently
 *  reimplement their own cutoffs today (SecurityObjectivesPage.tsx, IsmsJourneyPage.tsx at
 *  75/40, ReportsPage.tsx at 80/50), and the first two use a success/info-or-primary/warning
 *  vocabulary with no danger tone at all, so they aren't real precedent for a 3-severity scale -
 *  based on ReportsPage.tsx's 80/50 cutoffs, the one that actually uses success/warning/danger. */
export const scoreTone = (pct: number): Tone => (pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'danger');

// Shared between every page that renders the Portfolio/Program/Project tree
// (AllInitiativesPage.tsx, OverviewPage.tsx) so the tier rail/badge/tone treatment can
// never drift between them the way the same map used to be redefined per file.
export const TIER_TONE: Record<string, string> = { Portfolio: 'primary', Program: 'info', Project: 'neutral' };
export const TIER_ICON: Record<string, string> = { Portfolio: 'ri-stack-line', Program: 'ri-flag-line', Project: 'ri-file-list-3-line' };
/** Lowercase, CSS-class-safe form of a tier, folding anything unrecognised (including no
 *  tier at all) to 'none' rather than letting an unexpected value produce a broken classname. */
export const tierSlug = (tier?: string): 'portfolio' | 'program' | 'project' | 'none' =>
  tier === 'Portfolio' || tier === 'Program' || tier === 'Project' ? (tier.toLowerCase() as 'portfolio' | 'program' | 'project') : 'none';

export const STATUS_COLOR: Record<string, Tone> = {
  'Not Started': 'neutral',
  'In Progress': 'info',
  Blocked: 'danger',
  'On Hold': 'warning',
  Completed: 'success',
  Cancelled: 'neutral',
};

// NOTE: still consumed directly (without toneFromMap) by InitiativeDetailPage.tsx, which
// is out of scope for this change - left untouched rather than deleted/retyped so that
// file's build doesn't break. AllInitiativesPage.tsx no longer imports this; see
// SEVERITY_TONE in constants/tone.ts for its replacement there.
export const PRIORITY_COLOR: Record<string, string> = {
  Low: 'secondary',
  Medium: 'info',
  High: 'warning',
  Critical: 'danger',
};

export const CATEGORY_COLOR: Record<string, Tone> = {
  GRC: 'primary',
  'Platform DevSecOps': 'info',
  'SecOps/Enterprise IT': 'danger',
  'Service Desk': 'neutral',
  'Cross-functional': 'success',
};

export const CATEGORIES = ['GRC', 'Platform DevSecOps', 'SecOps/Enterprise IT', 'Service Desk', 'Cross-functional'];

/**
 * Weighted completion (%) across a set of milestones, normalized against however much
 * weight has actually been allocated. Returns null when no milestone carries a weight,
 * meaning the feature isn't in use for this initiative.
 */
/**
 * How far along a single milestone is (0-100). Uses its own completionPercent when set;
 * otherwise falls back to the binary Done=100 / anything else=0, so milestones created
 * before this field existed still behave the same as before.
 */
export const milestoneProgress = (m: { status: string; completionPercent?: number }): number => {
  if (typeof m.completionPercent === 'number') return Math.max(0, Math.min(100, m.completionPercent));
  return m.status === 'Done' ? 100 : 0;
};

export const milestoneCompletion = (milestones: { status: string; weight?: number; completionPercent?: number }[]): number | null => {
  const totalWeight = milestones.reduce((sum, m) => sum + (m.weight || 0), 0);
  if (totalWeight <= 0) return null;
  const weightedProgress = milestones.reduce((sum, m) => sum + (m.weight || 0) * (milestoneProgress(m) / 100), 0);
  return Math.round((weightedProgress / totalWeight) * 100);
};

export type MetricStatus = 'on-target' | 'off-target' | 'no-target' | 'no-data';

export const METRIC_STATUS_COLOR: Record<MetricStatus, string> = {
  'on-target': 'success',
  'off-target': 'danger',
  'no-target': 'secondary',
  'no-data': 'secondary',
};

/** Shared between CatalogPage.tsx (full editor) and HubServiceCatalog.tsx (Hub's read-only
 * view), so "on target" means the same thing in both places rather than two copies of this
 * comparison drifting apart. */
export const computeMetricStatus = (metric: { target?: number; direction: string }, readings: { value: number }[]): MetricStatus => {
  if (readings.length === 0) return 'no-data';
  if (metric.target == null) return 'no-target';
  const latest = readings[readings.length - 1].value;
  const onTarget = metric.direction === 'higher' ? latest >= metric.target : latest <= metric.target;
  return onTarget ? 'on-target' : 'off-target';
};

/** Same fixed 4-level scale as SEVERITY_TONE (constants/tone.ts) - Critical carries the most
 *  weight in Operational Health below, same as it would in any other risk-adjacent register. */
export const CRITICALITY_WEIGHT: Record<string, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };

/** A Program/Portfolio's Operational Health - how healthy what it's actually running/
 *  maintaining is, from the initiative's OWN linked Service Catalog metrics (not its
 *  children's - a Program is already expected to carry its own targetMetricIds, same as a
 *  Project is). score is a criticality-weighted on-target ratio, excluding no-target/no-data
 *  metrics from both sides since those are unmeasured, not failures.
 *
 *  floor is a severity FLOOR, not a cap - "even if one metric is out, it's at risk" means the
 *  displayed label can never look BETTER than the floor, but the floor must never make an
 *  otherwise-worse numeric label look better either. Callers take whichever of
 *  (scoreTone-derived label, floor) is more severe - see InitiativeDetailPage.tsx's Health
 *  Rollup card for exactly how the two combine. */
// Metric objects are untyped `any` at every call site in this codebase (metricsApi.getAll
// returns any[]); statusOf's caller (computeMetricStatus) needs target/direction, which a
// narrower type here would lose - matches this file's existing any-typed Metric handling.
export const operationalHealth = (
  metrics: any[],
  statusOf: (metric: any) => MetricStatus,
): { score: number | null; floor: 'Blocked' | 'At Risk' | null } => {
  const measured = metrics
    .map((m) => ({ m, status: statusOf(m) }))
    .filter(({ status }) => status === 'on-target' || status === 'off-target');
  const totalWeight = measured.reduce((sum, { m }) => sum + CRITICALITY_WEIGHT[m.criticality || 'Medium'], 0);
  const score = totalWeight > 0
    ? Math.round(
        measured.reduce((sum, { m, status }) => sum + (status === 'on-target' ? CRITICALITY_WEIGHT[m.criticality || 'Medium'] : 0), 0)
          / totalWeight * 100,
      )
    : null;
  const anyOffTargetAt = (crit: string) => measured.some(({ m, status }) => status === 'off-target' && (m.criticality || 'Medium') === crit);
  const floor = anyOffTargetAt('Critical') ? 'Blocked' : anyOffTargetAt('High') ? 'At Risk' : null;
  return { score, floor };
};
